/** Who is signed in. The user object comes from GET /me and is refreshed after changes. */
import { api } from './api.js';
import { bus } from './bus.js';
import { config } from './config.js';
import { tokens } from './tokens.js';

let user = null;

export const session = {
  get user() {
    return user;
  },
  get signedIn() {
    return user !== null;
  },
  get role() {
    return user?.role ?? null;
  },

  /** Restore a session after a page reload. Resolves to the user or null. */
  async restore() {
    if (!tokens.refresh && !tokens.access) return null;
    try {
      // The access token lives in memory, so after a reload there is none: renew first instead of probing with a 401.
      if (!tokens.access) await api.refresh();
      const { user: me } = await api.get('/me');
      user = me;
    } catch (error) {
      if (error.status === 401 || error.status === 403) tokens.clear();
      user = null; // other failures keep the stored token, so a reload once the server is back signs in again
    }
    return user;
  },

  /** Called with the body of /auth/login or /auth/otp/verify. */
  start(body) {
    tokens.set(body);
    user = body.user;
    bus.emit('session:changed', user);
  },

  async reload() {
    const { user: me } = await api.get('/me');
    user = me;
    bus.emit('session:changed', user);
    return user;
  },

  /** Sign out of this device. The server revokes the refresh token on a best-effort basis. */
  async end() {
    const refresh = tokens.refresh;
    tokens.clear();
    user = null;
    bus.emit('session:changed', null);
    if (refresh) {
      try {
        await fetch(`${config.apiBase}/auth/signout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${refresh}` },
        });
      } catch {
        /* the token expires on its own; nothing to do */
      }
    }
  },

  /** Drop local state without calling the server (the server already ended the session). */
  clear() {
    tokens.clear();
    user = null;
    bus.emit('session:changed', null);
  },
};

/** Where each role lands after login. */
export function homeFor(role) {
  if (!role) return '/';
  return role === 'admin' ? '/admin/overview' : `/${role}/dashboard`;
}
